import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Phone, Envelope, MapPin, SignOut, Trash, Plus, PencilSimple, Coins } from '@phosphor-icons/react';
import { getProfile, updateProfile, getAddresses, addAddress, deleteAddress, setPhoneNumber, getMyWallet } from '../../services/customerApi';
import { useCustomer, PageWrap } from './CustomerApp';
import { toast } from 'sonner';

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { customer, logoutCustomer } = useCustomer();
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddr, setNewAddr] = useState({ label: 'Home', address_line: '', landmark: '', city: '', pincode: '' });

  useEffect(() => {
    loadProfile();
    loadWallet();
  }, []);

  const loadWallet = async () => {
    try {
      const res = await getMyWallet();
      setWallet(res.data);
    } catch {
      // Non-critical — profile still works without wallet data
    }
  };

  const loadProfile = async () => {
    try {
      const res = await getProfile();
      setProfile(res.data);
      setName(res.data.name || '');
      setEmail(res.data.email || '');
      setPhone(res.data.phone || '');
      setAddresses(res.data.addresses || []);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (phone && phone.replace(/\D/g, '').length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    try {
      await updateProfile({ name, email });
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone && cleanPhone !== profile?.phone) {
        await setPhoneNumber(cleanPhone);
      }
      setEditingName(false);
      toast.success('Profile updated');
      // Update localStorage
      const user = JSON.parse(localStorage.getItem('customer_user') || '{}');
      user.name = name;
      user.email = email;
      user.phone = cleanPhone;
      localStorage.setItem('customer_user', JSON.stringify(user));
      loadProfile();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleAddAddress = async () => {
    if (!newAddr.address_line.trim()) { toast.error('Enter an address'); return; }
    try {
      const res = await addAddress({ ...newAddr, is_default: addresses.length === 0 });
      setAddresses((prev) => [...prev, res.data.address]);
      setShowAddAddress(false);
      setNewAddr({ label: 'Home', address_line: '', landmark: '', city: '', pincode: '' });
      toast.success('Address added');
    } catch {
      toast.error('Failed to add address');
    }
  };

  const handleDeleteAddress = async (id) => {
    try {
      await deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      toast.success('Address removed');
    } catch {
      toast.error('Failed to remove address');
    }
  };

  const handleLogout = () => {
    logoutCustomer();
    navigate('/order/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF9]">
        <div className="w-8 h-8 border-3 border-[#E21836] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <PageWrap>
      <div className="min-h-screen pb-24" data-testid="profile-page">
        <div className="sticky top-0 z-40 bg-[#FFFDF9]/80 backdrop-blur-xl border-b border-[#E8E2D6]/50 px-4 py-3">
          <h2 className="text-lg font-bold text-[#2D231E]">Profile</h2>
        </div>

        <div className="px-4 pt-4 space-y-4">
          {/* Profile Card */}
          <div className="p-4 bg-[#F6F3EC] border border-[#E8E2D6] rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              {profile?.picture ? (
                <img src={profile.picture} alt={profile.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#E21836]/10 flex items-center justify-center">
                  <User size={24} weight="duotone" className="text-[#E21836]" />
                </div>
              )}
              <div className="flex-1">
                {profile?.email && (
                  <div className="flex items-center gap-2">
                    <Envelope size={14} className="text-[#6C5D53]" />
                    <span className="text-sm text-[#2D231E] font-medium">{profile.email}</span>
                  </div>
                )}
                {profile?.phone && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <Phone size={14} className="text-[#6C5D53]" />
                    <span className="text-xs text-[#6C5D53]">{profile.phone}</span>
                  </div>
                )}
              </div>
              <button
                data-testid="edit-profile-btn"
                onClick={() => setEditingName(!editingName)}
                className="p-2 rounded-full hover:bg-[#E8E2D6] transition-colors"
              >
                <PencilSimple size={16} className="text-[#6C5D53]" />
              </button>
            </div>

            {editingName ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
                <input
                  data-testid="profile-name-input"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                />
                <input
                  data-testid="profile-email-input"
                  placeholder="Email (optional)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                />
                <input
                  data-testid="profile-phone-input"
                  placeholder="10-digit mobile number"
                  type="tel"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                />
                <button
                  data-testid="save-profile-btn"
                  onClick={handleUpdateProfile}
                  className="w-full py-2.5 bg-[#E21836] text-white text-sm font-semibold rounded-xl"
                >
                  Save
                </button>
              </motion.div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-[#6C5D53]" />
                  <span className="text-sm text-[#2D231E]">{name || 'Add your name'}</span>
                </div>
                {email && (
                  <div className="flex items-center gap-2">
                    <Envelope size={14} className="text-[#6C5D53]" />
                    <span className="text-sm text-[#6C5D53]">{email}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Loyalty Wallet */}
          {wallet && (
            <div
              className="p-4 bg-gradient-to-br from-[#E21836] to-[#C8122D] rounded-2xl text-white cursor-pointer"
              data-testid="wallet-card"
              onClick={() => navigate('/order/wallet')}
            >
              <div className="flex items-center gap-2 mb-1">
                <Coins size={20} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wide opacity-90">Loyalty Wallet</span>
              </div>
              <p className="text-3xl font-bold mb-1">{wallet.redeemable_now} pts</p>
              <p className="text-xs opacity-80">worth ₹{wallet.redeemable_value_rupees} on your next order</p>
              {wallet.pending_until_tomorrow > 0 && (
                <p className="text-[11px] opacity-70 mt-2">
                  +{wallet.pending_until_tomorrow} pts unlock tomorrow
                </p>
              )}
              {wallet.soonest_expiring_points > 0 && (
                <p className="text-[11px] opacity-70 mt-0.5">
                  {wallet.soonest_expiring_points} pts expire {new Date(wallet.soonest_expiring_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </p>
              )}
              <p className="text-[11px] opacity-90 mt-3 underline">View full wallet &amp; history →</p>
            </div>
          )}

          {/* Addresses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6C5D53]">Saved Addresses</h3>
              <button
                data-testid="add-address-profile-btn"
                onClick={() => setShowAddAddress(!showAddAddress)}
                className="flex items-center gap-1 text-xs font-semibold text-[#E21836]"
              >
                <Plus size={14} weight="bold" /> Add
              </button>
            </div>

            {showAddAddress && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-3 space-y-2">
                <div className="flex gap-2">
                  {['Home', 'Work', 'Other'].map((lbl) => (
                    <button
                      key={lbl}
                      onClick={() => setNewAddr((p) => ({ ...p, label: lbl }))}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        newAddr.label === lbl ? 'bg-[#E21836] text-white' : 'bg-[#F6F3EC] text-[#6C5D53] border border-[#E8E2D6]'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <input
                  placeholder="Full address"
                  value={newAddr.address_line}
                  onChange={(e) => setNewAddr((p) => ({ ...p, address_line: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    placeholder="Landmark"
                    value={newAddr.landmark}
                    onChange={(e) => setNewAddr((p) => ({ ...p, landmark: e.target.value }))}
                    className="px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                  />
                  <input
                    placeholder="Pincode"
                    value={newAddr.pincode}
                    onChange={(e) => setNewAddr((p) => ({ ...p, pincode: e.target.value }))}
                    className="px-3 py-2.5 bg-[#FFFDF9] border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                  />
                </div>
                <button onClick={handleAddAddress} className="w-full py-2.5 bg-[#2D231E] text-white text-sm font-semibold rounded-xl">
                  Save Address
                </button>
              </motion.div>
            )}

            <div className="space-y-2">
              {addresses.length === 0 ? (
                <p className="text-sm text-[#6C5D53] py-4 text-center">No saved addresses</p>
              ) : (
                addresses.map((addr) => (
                  <div key={addr.id} className="flex items-start gap-2 p-3 bg-[#F6F3EC] border border-[#E8E2D6] rounded-xl" data-testid={`profile-address-${addr.id}`}>
                    <MapPin size={16} weight="duotone" className="text-[#E21836] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-[#2D231E]">{addr.label}</span>
                      <p className="text-xs text-[#6C5D53] truncate">{addr.address_line}</p>
                    </div>
                    <button
                      data-testid={`delete-address-${addr.id}`}
                      onClick={() => handleDeleteAddress(addr.id)}
                      className="p-1 text-[#D32F2F] hover:bg-[#D32F2F]/10 rounded-full"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Logout */}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#D32F2F]/10 text-[#D32F2F] font-semibold rounded-xl mt-4"
          >
            <SignOut size={18} /> Logout
          </button>
        </div>
      </div>
    </PageWrap>
  );
}
